/**
 * iewcasia override of SignIn/SignInExternalProviders for Azure B2C SSO.
 * - Removes the OOTB horizontal rule above the SSO section.
 * - Title is CMS-controlled (empty Title field falls back to a default heading).
 * - Own email field: SSO button stays disabled until a valid email is entered (login_hint).
 * - ?username=email pre-fills the SSO field (auto-redirect is owned by SignInPage).
 */
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { ExternalProviderLinkModel } from "@insite/client-framework/Services/IdentityService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signInExternalProvider from "@insite/client-framework/Store/Context/Handlers/SignInExternalProvider";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadExternalProviders from "@insite/client-framework/Store/Pages/SignIn/Handlers/LoadExternalProviders";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.fullWidthButton]: boolean;
    };
}

/** Maps Redux state used by external SSO provider buttons. */
const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ username?: string }>(location.search);
    const usernameFromQuery = parsedQuery.username?.trim() || "";

    return {
        externalProviders: state.pages.signIn.externalProviders,
        // Per-website Graph Integration Connector → Enable Storefront SSO (not global).
        enableStorefrontSso: !!(getSettingsCollection(state) as any).azureB2CGraphSettings?.enableStorefrontSso,
        usernameFromQuery,
    };
};

const mapDispatchToProps = {
    loadExternalProviders,
    signInExternalProvider,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SignInExternalProvidersStyles {
    titleText?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldProps;
    providerButtonGridItem?: GridItemProps;
    providerButton?: ButtonPresentationProps;
}

export const signInExternalProvidersStyles: SignInExternalProvidersStyles = {
    titleText: {
        variant: "h4",
    },
    emailGridItem: {
        width: 12,
    },
    providerButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
            flex-wrap: wrap;
        `,
    },
};

const styles = signInExternalProvidersStyles;

/** Renders Azure B2C / external SSO provider buttons on the Sign In page. */
const SignInExternalProviders = ({
    externalProviders,
    enableStorefrontSso,
    usernameFromQuery,
    loadExternalProviders,
    signInExternalProvider,
    fields,
}: Props) => {
    const [email, setEmail] = useState(usernameFromQuery);
    const [isEmailTouched, setIsEmailTouched] = useState(false);

    useEffect(() => {
        if (usernameFromQuery && usernameFromQuery !== email) {
            setEmail(usernameFromQuery);
        }
    }, [usernameFromQuery]);

    useEffect(() => {
        if (!enableStorefrontSso) {
            return;
        }

        if (!externalProviders) {
            loadExternalProviders();
        }
    }, [externalProviders, enableStorefrontSso]);

    const trimmedEmail = email.trim();
    const isEmailValid = validateEmail(trimmedEmail);
    const isSsoDisabled = !isEmailValid;
    const emailErrorMessage =
        isEmailTouched && trimmedEmail && !isEmailValid ? siteMessage("SignInInfo_Email_Validation") : "";

    /** Starts the external provider SSO flow when a valid email is present. */
    const providerButtonClickHandler = (externalProvider: ExternalProviderLinkModel) => {
        if (isSsoDisabled) {
            return;
        }

        signInExternalProvider({ externalProvider });
    };

    if (!enableStorefrontSso || !externalProviders || externalProviders.length === 0) {
        return null;
    }

    // CMS Title field; empty value falls back to a default heading (same pattern as OOB Sign In title).
    const title = fields.title?.trim() || translate("Use another service to log in.");

    const providerButtonCss = css`
        margin: 0;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <>
            <Typography {...styles.titleText}>{title}</Typography>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        id="ssoUserName"
                        {...styles.emailTextField}
                        label={translate("Email")}
                        onChange={e => {
                            setEmail(e.currentTarget.value);
                            if (!isEmailTouched) {
                                setIsEmailTouched(true);
                            }
                        }}
                        value={email}
                        error={emailErrorMessage}
                        autoComplete="username"
                        data-test-selector="signInExternal_email"
                    />
                </GridItem>
                <GridItem {...styles.providerButtonGridItem}>
                    {externalProviders.map(externalProvider => (
                        <Button
                            {...styles.providerButton}
                            css={providerButtonCss}
                            key={externalProvider.caption}
                            disabled={isSsoDisabled}
                            onClick={() => providerButtonClickHandler(externalProvider)}
                        >
                            {externalProvider.caption}
                        </Button>
                    ))}
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SignInExternalProviders),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        displayName: "External Providers",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
